<script>
  import "paxray-components-svelte/css/all.css";
  import { onDestroy, onMount, setContext } from "svelte";
  import { ToastNotification } from "carbon-components-svelte";
  import { fly } from "svelte/transition";
  import { Router, Route, globalHistory } from "svelte-navigator";
  import { Session } from "svelte-session-manager";
  import { writable } from "svelte/store";
  import { userStores } from "@stores/index.js";
  import { UserService } from "@services/index.js";
  import { projectListStore } from "@stores/damProjectsStore.js";
  import { ROUTES } from "@utils/routes.js";
  import { noop } from "svelte/internal";
  import Toast from "./lib/components/Toast.svelte";
  import { toast } from "./lib/stores/toastStore.js";

  // New UI Routes
  import LoginScreen from "./routes/LoginScreenV2.svelte";
  import AppLayout from "./lib/components/layout/app/AppLayout.svelte";
  import PageTitle from "./lib/components/layout/PageTitle.svelte";
  import Index from "./routes/app/Index.svelte";
  import FrameworkAssistant from "./routes/app/FrameworkAssistant.svelte";
  import TeamSurvey from "./routes/app/damprojects-frontend/TeamSurvey.svelte";

  // vorherige Imports

  import Setup from "./routes/Setup.svelte";
  import UserInput from "./routes/UserInput.svelte";

  import PMANIndex from "./routes/app/processmodeler-frontend/Index.svelte";

  import DamIndex from "./routes/app/damprojects-frontend/Index.svelte";
  import DamProject from "./routes/app/damprojects-frontend/Project.svelte";
  import Analysis from "./routes/app/analysis-frontend/Analysis.svelte";
  import DamAnalysis from "./routes/app/damprojects-frontend/Analysis.svelte";
  import PreProcessing from "./routes/app/analysis-frontend/PreProcessing.svelte";
  import Progress from "./routes/app/damprojects-frontend/Progress.svelte";
  import Admin from "./routes/app/Admin.svelte";
  import UserHistory from "./routes/app/settings/UserHistory.svelte";
  import { loadUiFeatures } from "@stores/uiFeatureStore.js";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";

  import Redirect from "./lib/components/Redirect.svelte";
  import AnonymizationPresets from "./routes/app/damprojects-frontend/presets/AnonymizationPresets.svelte";
  import LimitationPresets from "./routes/app/damprojects-frontend/presets/LimitationPresets.svelte";
  import ProcessPresets from "./routes/app/damprojects-frontend/ProcessPresets.svelte";
  import InstallationInstructions from "./routes/app/damprojects-frontend/InstallationInstructions.svelte";
  import AppnameMapping from "./routes/app/damprojects-frontend/AppnameMapping.svelte";
  import TaskFrequencyMapping from "./routes/app/damprojects-frontend/TaskFrequencyMapping.svelte";
  import MappingAssistant from "./routes/app/analysis-frontend/MappingAssistant.svelte";

  import SettingsIndex from "./routes/app/settings/Index.svelte";

  import Presets from "./routes/app/damprojects-frontend/Presets.svelte";

  import UsersList from "./routes/app/settings/UsersList.svelte";
  import TeamsList from "./routes/app/settings/TeamsList.svelte";
  import ProcessManagementList from "./routes/app/settings/ProcessManagementList.svelte";
  import DataList from "./routes/app/settings/DataList.svelte";
  import Error404 from "./routes/Error404.svelte";
  import { jwtDecode } from "jwt-decode";
  import GroupsList from "./routes/app/settings/GroupsList.svelte";
  import SensorInfo from "./routes/app/damprojects-frontend/SensorInfo.svelte";

  import TimeIndex from "./routes/app/timetracking-frontend/Index.svelte";
  import KeywordSupport from "./routes/app/damprojects-frontend/Project/KeywordSupport.svelte";
  import CaseIdAssistant from "./routes/app/damprojects-frontend/Project/CaseIdAssistant.svelte";
  import AnonymizationAssistant from "./routes/app/damprojects-frontend/Project/AnonymizationAssistant.svelte";
  import NumberRangeAssistant from "./routes/app/damprojects-frontend/Project/NumberRangeAssistant.svelte";
  import ProcessRegistration from "./routes/app/damprojects-frontend/Project/ProcessRegistration.svelte";

  import DataDeletion from "./routes/app/damprojects-frontend/DataDeletion.svelte";

  import KnowledgeIndex from "./routes/app/knextractor-frontend/Index.svelte";
  import KnowledgeFormBuilder from "./routes/app/knextractor-frontend/FormBuilder.svelte";
  import KnowledgeSurvey from "./routes/app/knextractor-frontend/SurveyRouter.svelte";
  import KnowledgePresets from "./routes/app/knextractor-frontend/Presets.svelte";
  import KnowledgeUsersList from "./routes/app/knextractor-frontend/UserList.svelte";
  import PotentialScope from "./routes/app/knextractor-frontend/PotentialScope.svelte";
  import MonitoringDashboard from "./routes/app/monitoring-frontend/Dashboard.svelte";
  import MonitoringCatalog from "./routes/app/monitoring-frontend/Index.svelte";
  import MonitoringLicenseCatalog from "./routes/app/monitoring-frontend/LicenseCatalog.svelte";
  import MonitoringChangeAnalysis from "./routes/app/monitoring-frontend/ChangeAnalysis.svelte";
  import MonitoringCategoryDeepDive from "./routes/app/monitoring-frontend/CategoryDeepDive.svelte";
  import ProcessMonitoringDashboard from "./routes/app/monitoring-frontend/ProcessDashboard.svelte";
  import ProcessMonitoringCatalog from "./routes/app/monitoring-frontend/ProcessCatalog.svelte";
  import ProcessMonitoringDeepDive from "./routes/app/monitoring-frontend/ProcessDeepDive.svelte";
  import ProcessMonitoringChangeAnalysis from "./routes/app/damprojects-frontend/analysis/changeAnalysis.svelte";
  import Dashboard from "./routes/app/damprojects-frontend/Project/Dashboard.svelte";
  import TeamSettings from "./routes/app/damprojects-frontend/Project/TeamSettings.svelte";
  import Blacklisting from "./routes/app/damprojects-frontend/Project/Blacklisting.svelte";
  import Whitelisting from "./routes/app/damprojects-frontend/Project/Whitelisting.svelte";
  import WhitelistPhase from "./routes/app/damprojects-frontend/Project/WhitelistPhase.svelte";
  import EndWhitelistPhase from "./routes/app/damprojects-frontend/Project/EndWhitelistPhase.svelte";
  import StartDataCollection from "./routes/app/damprojects-frontend/Project/StartDataCollection.svelte";
  import StartTaskRegistration from "./routes/app/damprojects-frontend/Project/StartTaskRegistration.svelte";
  import EndTaskRegistration from "./routes/app/damprojects-frontend/Project/EndTaskRegistration.svelte";
  import Workshop from "./routes/app/damprojects-frontend/Project/Workshop.svelte";

  const { getProjectName, loadProjectName } = projectListStore;

  // Session singleton
  let session = new Session(localStorage);
  let sessionIsValid = session.isValid; // reactive variable for App.svelte routing

  let role = writable({ role: "viewer" });
  let permissions = [];
  // Context for the session
  setContext("session", session);
  setContext("sessionUpdate", () => {
    sessionIsValid = session.isValid; // function to trigger re-evaluation in App.svelte
  });
  setContext("role", role);

  let urlParams = new URLSearchParams(window.location.search);
  let idproject = urlParams.get("idproject");
  let analysisid = urlParams.get("analysisid");

  $: if (idproject) {
    getProjectName(idproject);
  } else if (analysisid) {
    loadProjectName(analysisid);
  } else {
    loadProjectName(null);
  }

  $: ({ currentProjectName } = $projectListStore);
  $: projectName = currentProjectName || "";

  // Follows client-side navigation, so leaving a public route like UserInput mounts the app routes.
  let currentPath = window.location.pathname;
  const stopPathListener = globalHistory.listen(({ location }) => {
    currentPath = location.pathname;
  });
  onDestroy(stopPathListener);

  $: publicRoutesTitles = {
    [ROUTES.setup]: "Paxray · Setup",
    [ROUTES.fallback]: "Sign In • paxray",
    [ROUTES.processRegistration.dashboard]: "Paxray · Dashboard",
    [ROUTES.userInput]: "Paxray · User Input",
    [ROUTES.dam.dataDeletion]: "Paxray · Data Deletion",
    [ROUTES.dataDeletion]: "Paxray · Data Deletion",
    [ROUTES.knex.survey]: "Paxray · Survey",
    [ROUTES.teamSurvey]: "Task Listing • paxray",
  };

  $: publicTitle = publicRoutesTitles[currentPath];

  $: if (sessionIsValid && session.authorizationHeader?.Authorization) {
    try {
      $role = jwtDecode(session.authorizationHeader.Authorization);
    } catch (e) {
      console.error("Failed to decode token", e);
    }
  } else if (!sessionIsValid) {
    $role = { role: "viewer" };
  }

  $: setPermissions($userStores?.groups);

  function setPermissions(groups) {
    permissions = new Set();
    groups
      ?.map((group) => group.permissions)
      .forEach((perms) => {
        perms.forEach((p) => permissions.add(p));
      });
    permissions = Array.from(permissions);
  }

  const globalErrorObject = writable({
    errorNotification: false,
    errorMessage: "",
  });

  $: {
    if ($globalErrorObject.errorNotification) {
      window.setTimeout(() => {
        $globalErrorObject.errorNotification = false;
      }, 2000);
    }
  }

  // expose toast in context
  setContext("toast", globalErrorObject);

  // user
  const userService = new UserService();
  const setUserInstore = () => {
    userService
      .getLoggedInUser()
      .then((user) => {
        if (user && user?.id) {
          userStores.set(user);
          loadUiFeatures();
          fetchWithSession("/api/damprojects/user-history/activity", {
            method: "POST",
          }).catch(() => {});
        }
      })
      .catch((error) => {
        session.invalidate();
        sessionIsValid = session.isValid; // Update the UI if token was rejected
      });
  };

  const setUserStoreInContext = (user) => {
    setContext("user", user);
  };
  setUserStoreInContext(userStores);

  $: {
    if (sessionIsValid) {
      setUserInstore();
    } else {
      noop();
    }
  }
  onMount(() => {
    window.onunhandledrejection = function (e) {
      // A request cancelled because its view was left is expected, not a fault.
      if (e.reason?.name === "AbortError") return;
      if ($toast.message) return;
      $globalErrorObject.errorMessage = e.reason.message;
      $globalErrorObject.errorNotification = true;
    };
  });
</script>

<svelte:head>
  {#if publicTitle}
    <title>{publicTitle}</title>
  {:else if !sessionIsValid}
    <title>Sign In • paxray</title>
  {/if}

  <meta name="robots" content="noindex nofollow" />
  <html lang="en" />
</svelte:head>

{#if $globalErrorObject.errorNotification}
  <div
    in:fly={{ x: 100 }}
    out:fly={{ y: 100 }}
    style="position:absolute; top:{$toast.message ? '180px' : '60px'}; right:20px; z-index:4000;"
  >
    <ToastNotification
      kind="error"
      title="Error"
      subtitle={$globalErrorObject.errorMessage}
      caption={new Date().toLocaleString()}
      fullWidth
      on:close={(e) => {
        e.preventDefault();
        $globalErrorObject.errorNotification = false;
      }}
    />
  </div>
{/if}

<Toast />

<Router>
  <PageTitle />
  {#if !sessionIsValid}
    <Route path={ROUTES.setup} component={Setup} />
    <Route path={ROUTES.fallback} component={LoginScreen} />
    {#if currentPath === ROUTES.processRegistration.dashboard}
      <AppLayout>
        <Route path={ROUTES.processRegistration.dashboard} primary={false} />
        <Dashboard {idproject} />
      </AppLayout>
    {/if}
  {/if}
  {#if sessionIsValid && currentPath !== ROUTES.userInput && currentPath !== ROUTES.dam.dataDeletion && currentPath !== ROUTES.teamSurvey}
    <AppLayout>
      <Route path={ROUTES.setup}>
        <Redirect to={ROUTES.home} />
      </Route>
      <Route path={ROUTES.home} component={DamIndex} />
      <Route path={ROUTES.frameworkAssistant} component={FrameworkAssistant} />
      <Route path={ROUTES.app} component={DamIndex} />

      <Route path={ROUTES.dam.root} component={DamIndex} />
      <Route
        path={ROUTES.dam.installationInstructions}
        component={InstallationInstructions}
      />
      <Route path={ROUTES.dam.project} component={DamProject} />
      <Route path={ROUTES.dam.progress} component={Progress} primary={false} />
      <Route
        path={ROUTES.processRegistration.all}
        component={ProcessRegistration}
      />
      <Route path={ROUTES.dam.presets.root} component={Presets} />
      <Route
        path={ROUTES.dam.numberRangeAssistant}
        component={NumberRangeAssistant}
      />
      <Route
        path={ROUTES.dam.presets.anonymization}
        component={AnonymizationPresets}
      />
      <Route
        path={ROUTES.dam.presets.limitation}
        component={LimitationPresets}
      />
      <Route path={ROUTES.dam.appnamemapping} component={AppnameMapping} />
      <Route
        path={ROUTES.dam.taskfrequencymapping}
        component={TaskFrequencyMapping}
      />

      <Route path={ROUTES.analysis.all} component={Analysis} />
      <Route path={ROUTES.dam.analysis.all} component={DamAnalysis} />
      <Route path={ROUTES.preprocessing.all} component={PreProcessing} />
      <Route path={ROUTES.dam.mappingAssistant} component={MappingAssistant} />
      <Route path={ROUTES.dam.dataDeletion} component={DataDeletion} />
      <Route path={ROUTES.dataDeletion} component={DataDeletion} />
      <Route path={ROUTES.dam.sensorInfo} component={SensorInfo} />
      <Route path={ROUTES.dam.processPresets} component={ProcessPresets} />
      <Route path={ROUTES.dam.companySettings.root} component={ProcessPresets} />
      <Route path={ROUTES.dam.companySettings.all} component={ProcessPresets} />
      <Route path={ROUTES.dam.teamSettings} component={TeamSettings} />
      <Route path={ROUTES.dam.blacklisting} component={Blacklisting} />
      <Route path={ROUTES.dam.whitelisting} component={Whitelisting} />
      <Route path={ROUTES.dam.whitelistPhase} component={WhitelistPhase} />
      <Route path={ROUTES.dam.endWhitelistPhase} component={EndWhitelistPhase} />
      <Route
        path={ROUTES.dam.startDataCollection}
        component={StartDataCollection}
      />
      <Route
        path={ROUTES.dam.StartTaskRegistration}
        component={StartTaskRegistration}
      />
      <Route
        path={ROUTES.dam.endTaskRegistration}
        component={EndTaskRegistration}
      />
      <Route path={ROUTES.dam.workshop} component={Workshop} />
      <Route
        path={ROUTES.dam.keywordAssistant}
        component={KeywordSupport}
        primary={false}
      />
      <Route
        path={ROUTES.dam.caseIdAssistant}
        component={CaseIdAssistant}
        primary={false}
      />
      <Route
        path={ROUTES.dam.anonymizationAssistant}
        component={AnonymizationAssistant}
        primary={false}
      />

      <Route path={ROUTES.processModeler} component={PMANIndex} />

      <Route path={ROUTES.knex.root} component={KnowledgeIndex} />
      <Route path={ROUTES.knex.form} component={KnowledgeFormBuilder} />
      <Route path={ROUTES.knex.survey} component={KnowledgeSurvey} />
      <Route path={ROUTES.knex.presets} component={KnowledgePresets} />
      <Route path={ROUTES.knex.userList} component={KnowledgeUsersList} />
      <Route path={ROUTES.knex.potentialScope.all} component={PotentialScope} />
      <Route path={ROUTES.monitoring.root} component={MonitoringDashboard} />
      <Route path={ROUTES.monitoring.catalog} component={MonitoringCatalog} />
      <Route path={ROUTES.monitoring.licenseCatalog} component={MonitoringLicenseCatalog} />
      <Route
        path={ROUTES.monitoring.changeAnalysis}
        component={MonitoringChangeAnalysis}
      />
      <Route
        path={ROUTES.monitoring.categoryDeepDive}
        component={MonitoringCategoryDeepDive}
      />
      <Route
        path={ROUTES.monitoring.processRoot}
        component={ProcessMonitoringDashboard}
      />
      <Route
        path={ROUTES.monitoring.processCatalog}
        component={ProcessMonitoringCatalog}
      />
      <Route
        path={ROUTES.monitoring.processChangeAnalysis}
        component={ProcessMonitoringChangeAnalysis}
      />
      <Route
        path={ROUTES.monitoring.processDeepDive}
        component={ProcessMonitoringDeepDive}
      />

      <Route path={ROUTES.timeTracker} component={TimeIndex} />

      {#if $role?.role !== "viewer" || permissions.length > 0}
        <Route
          path={ROUTES.settings.root}
          component={SettingsIndex}
          primary={false}
        />
        <Route path={ROUTES.settings.userManagement} component={UsersList} />
        {#if $role?.role === "admin"}
          <Route path={ROUTES.settings.userHistory} component={UserHistory} />
        {/if}
        <Route
          path={ROUTES.settings.permissionManagement}
          component={GroupsList}
        />
        <Route path={ROUTES.settings.teamManagement} component={TeamsList} />
        <Route path={ROUTES.settings.dataManagement} component={DataList} />
        <Route
          path={ROUTES.settings.processManagement}
          component={ProcessManagementList}
        />
      {/if}
      <Route path={ROUTES.admin} component={Admin} />
      <Route path={ROUTES.fallback}>
        <Error404 />
      </Route>
    </AppLayout>
  {/if}
  {#if currentPath === ROUTES.dam.dataDeletion || currentPath === ROUTES.dataDeletion}
    <AppLayout>
      <Route path={ROUTES.dam.dataDeletion} component={DataDeletion} />
      <Route
        path={ROUTES.dataDeletion}
        component={DataDeletion}
        primary={false}
      />
    </AppLayout>
  {/if}
  {#if currentPath === ROUTES.userInput}
    <AppLayout>
      <Route path={ROUTES.userInput} component={UserInput} primary={false} />
    </AppLayout>
  {/if}
  <Route path={ROUTES.knex.survey} component={KnowledgeSurvey} />
  <Route path={ROUTES.teamSurvey} component={TeamSurvey} />
</Router>
